import { AccountType, IAccount } from "dto/access-management/account-dto";
import { IDisplayDeploymentGroup } from "dto/deployment/deployment-group-dto";
import { DEPLOYMENT_GROUP_ROUTE_CODES } from "pages/private/software/deployment-group";
import { hasPermission } from "pages/private/utils";
import React from "react";
import { useStores } from "store";
import { ITreeSelectDataSource } from "store/account-store";
export interface IManagedDeploymentGroups {
    displayDeploymentGroups: IDisplayDeploymentGroup[];
    hasModifyDeploymentGroupPermission: (
        deploymentGroup: IDisplayDeploymentGroup
    ) => boolean;
    selectedAccount: IAccount | null;
    selectAccountSource: ITreeSelectDataSource;
    setSelectedAccount: (account: IAccount | null) => void;
    hasModifyPermission: boolean;
}

interface UseManagedDeploymentGroupsProps {
    accountId?: number;
}

export const useManagedDeploymentGroups = (
    props: UseManagedDeploymentGroupsProps = {}
): IManagedDeploymentGroups => {
    const { accountId } = props;

    const stores = useStores();
    const { accountStore, deploymentGroupsStore, authStore } = stores;
    const { currentUser } = authStore;

    const [selectedAccount, setSelectedAccount] = React.useState<IAccount | null>(() => {
        if (accountId) {
            return accountStore.getAccountById(accountId);
        }

        if (currentUser.accountType === AccountType.CLIENT) {
            return accountStore.getAccountById(currentUser.accountId);
        }

        return null;
    });

    const selectAccountSource = React.useMemo<ITreeSelectDataSource>(() => {
        return accountStore.getSelectableTreeDataSource(
            Object.values(AccountType),
            [AccountType.CLIENT, AccountType.VAR, AccountType.DPU]
        );
    }, [accountStore.accountHierarchy]);

    const hasModifyPermission = hasPermission(
        DEPLOYMENT_GROUP_ROUTE_CODES.MODIFY,
        stores
    );

    const hasModifyDeploymentGroupPermission = (deploymentGroup: IDisplayDeploymentGroup): boolean => {
        if (!hasModifyPermission) return false;
        return !deploymentGroup.isSharedGroup;
    };

    const displayDeploymentGroups = React.useMemo<IDisplayDeploymentGroup[]>(() => {
        const availableGroups = deploymentGroupsStore.displayDeploymentGroups;

        if (
            !selectedAccount ||
            selectedAccount.accountType === currentUser.accountType
        ) {
            return availableGroups;
        }

        const clientUuids = accountStore
            .getClientsForAccount(selectedAccount.id)
            .map(account => account.uuid);

        return availableGroups.filter(
            group =>
                group.accountUuid === selectedAccount.uuid
                || clientUuids.includes(group.accountUuid)
        );
    }, [selectedAccount, deploymentGroupsStore.displayDeploymentGroups]);

    return {
        selectAccountSource,
        selectedAccount,
        setSelectedAccount,
        hasModifyPermission,
        displayDeploymentGroups,
        hasModifyDeploymentGroupPermission,
    };
};
